import React from "react";
import styled, { css } from "styled-components";
import SafeSiteImage, { transformImageUrl } from "./SafeSiteImage";
import Container from "./Container";
import Title from "./Title";
import Section from "./Section";
import Grid from "./Grid";
import LiteYouTubeEmbed from "react-lite-youtube-embed";
import "react-lite-youtube-embed/dist/LiteYouTubeEmbed.css";
import { HtmlContent } from "./Content";
import { graphql, useStaticQuery } from "gatsby";
import { Carousel } from "./Carousel";

const ContentAndMediaLandingPageStyles = styled.div`
  ${({ ...props }) => css`
    .content {
      margin-top: .5rem
    }
    .content-with-image__image {
      img,
      article,
      video {
        width: 100%;
        aspect-ratio: 7/4;
        object-fit: cover;
        border-radius: 4px;
        height: auto;
      }
    }
    .list {
      margin-top: .5rem
    }
    .list-item {
      display: flex;
      column-gap: 0.5rem;
      &:not(:first-child) {
        margin-top: .5rem
      }
    }
    .list-image img {
      object-fit: contain;
      width: 36px;
      height: 36px;
    }
    .button {
      margin-top: 1rem
    }
    .content .heading {
      display: none;
    }
    .mobile-heading {
      order: 1;
    }
    .media {
      order: 2;
    }
    .content {
      order: 3;
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      .mobile-heading {
        display: none;
      }
      .content .heading {
        display: block;
      }
      .mobile-heading,
      .media,
      .content {
        order: revert
      }
    }

    .swiper-wrapper {
      height: auto;
    }
  `}
`

export const ContentAndMediaLandingPage = ({
  title,
  titleLink,
  content,
  list,
  button,
  image,
  imageLink,
  placement,
  background,
  mediaType = 'image',
  oembed,
  video,
  videoPoster,
  // Carousel
  carouselImages,
  autoScroll,
  delay
}) => {
  let site = {
    siteMetadata: {
      siteUrl: 'https://safesitefacilities.co.uk'
    }
  }
  if (!process.env.STORYBOOK) {
    ({ site } = useStaticQuery(graphql`
      query SiteUrlQuery {
        site {
          siteMetadata {
            siteUrl
          }
        }
      }
    `))
  }

  const media = () => {
    switch (mediaType) {
      case 'image':
        const imageComponent = <SafeSiteImage image={image.mediaItemUrl} alt={image.altText} width="720" />
        
        if (imageLink) {
          return (<a href={imageLink.url} target={imageLink.target} title={imageLink.title}>
            {imageComponent}
          </a>)
        }

        return imageComponent
      case 'video':
        return <video autoPlay muted loop playsInline poster={transformImageUrl({siteUrl: site.siteMetadata.siteUrl, imageUrl: videoPoster?.mediaItemUrl, width: 1920 })}><source src={video}></source></video>
      case 'oembed':
        // Get ID from URL
        const id = oembed.split("=")[1];

        if (!id) {
          return <></>
        }
        return <LiteYouTubeEmbed
          id={id}
          params="rel=0"
          poster="maxresdefault"
          webp={true}
          aspectWidth={7}
          aspectHeight={4}
        />
      case 'carousel':
        return <Carousel
          images={carouselImages}
          fullWidth={true}
          autoScroll={autoScroll}
          delay={delay}
        />
    }
  }

  const titleComponent = () => {
    const titleInnerComponent = <Title title={title} classes="heading" />

    if (titleLink) {
      return (<a href={titleLink.url} target={titleLink.target} title={titleLink.title}>
        {titleInnerComponent}
      </a>)
    }

    return titleInnerComponent
  }

  return (
    <ContentAndMediaLandingPageStyles
      $placement={placement}
    >
      <Section grey={background === "grey"}>
        <Container>
          <Grid columnGap="loose" reverse={placement === "imageOnLeft"} className="grid">
            <Grid item columnGap="loose" className="mobile-heading">
              {titleComponent()}
            </Grid>
            <Grid item md="5" columnGap="loose" className="content">
              {titleComponent()}
              {content && <div className="content"><HtmlContent htmlString={content} /></div>}
              {list && list.length > 0 && (
                <ul className="list">
                  {list.map((listItem, index) => {
                    return (
                      <li key={`${listItem.content}${index}`} className="list-item">
                        <div className="list-image">
                          <SafeSiteImage
                            image={listItem.icon.mediaItemUrl}
                            alt={listItem.icon.altText}
                            width="36"
                            height="36"
                          />
                        </div>
                        <span
                          className="list-content"
                        >
                        <HtmlContent htmlString={listItem.content} />
                        </span>
                      </li>
                    )
                  })
                }
                </ul>
              )}
              {button?.url && (
                <a href={button.url} target={button.target} className="button"><HtmlContent htmlString={button.title} /></a>
              )}
            </Grid>
            <Grid item md="7" columnGap="loose" className="media">
              <div className="content-with-image__image">
                {media()}  
              </div>
            </Grid>
          </Grid>
        </Container>
      </Section>
    </ContentAndMediaLandingPageStyles>
  );
};
